import { ServiceItem } from "./types";
import { tryApiPaths } from "./httpClient";

// ==================== Services API ====================
// Usa /api/businesses/{publicId}/services (nova API com publicId direto)

// Helper para extrair array de resposta em vários formatos possíveis
function extractArray(response: unknown): ServiceItem[] {
  if (Array.isArray(response)) return response;
  const data = response as {
    services?: unknown;
    data?: unknown;
    result?: unknown;
  };
  if (Array.isArray(data.services)) return data.services as ServiceItem[];
  if (Array.isArray(data.data)) return data.data as ServiceItem[];
  if (Array.isArray(data.result)) return data.result as ServiceItem[];
  return [];
}

// Helper para limpar objeto antes de enviar (remove campos vazios/null/undefined)
function sanitizeService(service: Partial<ServiceItem>): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {};
  
  for (const [key, value] of Object.entries(service)) {
    // Skip undefined, null, empty strings
    if (value === undefined || value === null || value === "") {
      continue;
    }
    
    // Skip empty arrays
    if (Array.isArray(value) && value.length === 0) {
      continue;
    }
    
    sanitized[key] = value;
  }
  
  return sanitized;
}

export function getGbpServiceErrorMessage(error: unknown, fallback: string): string {
  const raw = error instanceof Error ? error.message : String(error ?? "");
  const jsonMessage = raw.match(/"message"\s*:\s*"([^"]+)"/)?.[1];

  if (jsonMessage) {
    return jsonMessage;
  }

  return raw && !raw.startsWith("tryApiPaths:") ? raw : fallback;
}

export const gbpServicesApi = {
  /**
   * Lista serviços de um negócio.
   * Tenta múltiplos paths: /api/businesses/{publicId}/services
   */
  async listServices(publicId: string): Promise<ServiceItem[]> {
    try {
      const r = await tryApiPaths(
        "get",
        [
          `/api/businesses/${publicId}/services`,
          `/businesses/${publicId}/services`,
        ],
        undefined,
        { gbpOnly: true }
      );
      console.log("[gbpServicesApi.listServices]", "success", publicId, r);
      return extractArray(r?.data || r);
    } catch (err) {
      console.error("[gbpServicesApi.listServices]", "error", err);
      return [];
    }
  },

  /**
   * Lista categorias de serviços de um negócio.
   * GET /api/businesses/{publicId}/services/categories
   */
  async listServiceCategories(publicId: string): Promise<string[]> {
    try {
      const r = await tryApiPaths(
        "get",
        [
          `/api/businesses/${publicId}/services/categories`,
          `/businesses/${publicId}/services/categories`,
        ],
        undefined,
        { gbpOnly: true }
      );
      const result = r?.data || r;
      return Array.isArray(result) ? result : [];
    } catch (err) {
      console.error("[gbpServicesApi.listServiceCategories]", "error", err);
      return [];
    }
  },

  /**
   * Cria um novo serviço.
   * POST /api/businesses/{publicId}/services
   */
  async createService(publicId: string, service: ServiceItem): Promise<ServiceItem[]> {
    try {
      const sanitized = sanitizeService(service);
      console.log("[gbpServicesApi.createService]", "sending", sanitized);
      const r = await tryApiPaths(
        "post",
        [
          `/api/businesses/${publicId}/services`,
          `/businesses/${publicId}/services`,
        ],
        sanitized,
        { gbpOnly: true }
      );
      console.log("[gbpServicesApi.createService]", "success", publicId, r);
      return extractArray(r?.data || r);
    } catch (err) {
      console.error("[gbpServicesApi.createService]", "error", err);
      throw err;
    }
  },

  /**
   * Atualiza serviços de um negócio.
   * PUT /api/businesses/{publicId}/services
   */
  async updateServices(publicId: string, services: ServiceItem[]): Promise<ServiceItem[]> {
    try {
      const sanitized = services.map(s => sanitizeService(s));
      console.log("[gbpServicesApi.updateServices]", "sending", sanitized);
      const r = await tryApiPaths(
        "put",
        [
          `/api/businesses/${publicId}/services`,
          `/businesses/${publicId}/services`,
        ],
        sanitized,
        { gbpOnly: true }
      );
      return extractArray(r?.data || r);
    } catch (err) {
      console.error("[gbpServicesApi.updateServices]", "error", err);
      throw err;
    }
  },
};
